// Svelte-free by design, so a `node:test` file can exercise it.

/**
 * La sede legale di una controparte, come la vuole la fattura elettronica.
 *
 * Copia speculare delle regole di src/domain/customer-sede.js: il frontend non
 * può importare codice del backend, e test/frontend-customer-sede.test.js
 * tiene allineate le due versioni. Qui servono a dire subito all'utente quale
 * campo non va, invece di farlo scoprire dalla risposta del server, e a
 * riconoscere le anagrafiche che una fattura non può ancora usare.
 */

export const SEDE_FIELDS = Object.freeze(['indirizzo', 'numeroCivico', 'cap', 'comune', 'provincia', 'nazione']);

/** Il CAP che la fattura elettronica vuole per una sede fuori dall'Italia. */
export const CAP_ESTERO = '00000';

export const NAZIONE_DEFAULT = 'IT';

const SUBSTANTIVE = ['indirizzo', 'numeroCivico', 'cap', 'comune', 'provincia'];
const LATIN = /^[\x20-\x7E\xA0-\xFF]+$/;
const BASIC_LATIN = /^[\x20-\x7E]+$/;

const text = (value) => String(value ?? '').trim();

/** Vero quando la sede non contiene niente che una fattura possa stampare. */
export function isSedeEmpty(sede) {
  if (!sede || typeof sede !== 'object') return true;
  return SUBSTANTIVE.every((field) => !text(sede[field]));
}

/** Vero quando manca qualcosa che la fattura non può omettere. */
export function isSedeIncomplete(sede) {
  if (isSedeEmpty(sede)) return true;
  if (!text(sede.indirizzo) || !text(sede.cap) || !text(sede.comune)) return true;
  const nazione = text(sede.nazione).toUpperCase() || NAZIONE_DEFAULT;
  return nazione === NAZIONE_DEFAULT && !text(sede.provincia);
}

/**
 * La sede su una riga, come la si legge su una busta: «Via del Corso 12,
 * 00186 Roma (RM)», con la nazione solo quando non è l'Italia.
 *
 * @returns {string} '' quando non c'è nulla da mostrare
 */
export function formatSede(sede) {
  if (isSedeEmpty(sede)) return '';
  const street = [text(sede.indirizzo), text(sede.numeroCivico)].filter(Boolean).join(' ');
  const provincia = text(sede.provincia);
  const nazione = text(sede.nazione).toUpperCase();
  const place = [
    text(sede.cap),
    text(sede.comune),
    provincia && `(${provincia.toUpperCase()})`,
    nazione && nazione !== NAZIONE_DEFAULT ? nazione : '',
  ].filter(Boolean).join(' ');
  return [street, place].filter(Boolean).join(', ');
}

/**
 * La sede da mandare al server: testo ripulito, sigle in maiuscolo, e un campo
 * vuoto mandato come stringa vuota, che il server legge come «cancella».
 */
export function sedePayload(sede) {
  const out = {};
  for (const field of SEDE_FIELDS) {
    const value = text(sede?.[field]).replace(/\s+/g, ' ');
    out[field] = field === 'provincia' || field === 'nazione' ? value.toUpperCase() : value;
  }
  if (!out.nazione) out.nazione = NAZIONE_DEFAULT;
  return out;
}

/**
 * Il primo problema di ogni campo, con la chiave del catalogo che lo spiega.
 * Una sede del tutto vuota non ha problemi: l'avviso per quella è un altro.
 *
 * @returns {Record<string, string>} campo → chiave i18n sotto `customers.sede.errors`
 */
export function sedeProblems(sede) {
  const problems = {};
  if (isSedeEmpty(sede)) return problems;
  const value = (field) => text(sede[field]);
  const nazione = value('nazione').toUpperCase() || NAZIONE_DEFAULT;

  for (const field of ['indirizzo', 'comune']) {
    if (!value(field)) problems[field] = 'required';
    else if (value(field).length > 60) problems[field] = 'tooLong';
    else if (!LATIN.test(value(field))) problems[field] = 'charset';
  }

  const civico = value('numeroCivico');
  if (civico && (civico.length > 8 || !BASIC_LATIN.test(civico))) problems.numeroCivico = 'numeroCivico';

  const cap = value('cap');
  if (!cap) problems.cap = 'required';
  else if (!/^\d{5}$/.test(cap)) problems.cap = 'cap';
  else if (nazione !== NAZIONE_DEFAULT && cap !== CAP_ESTERO) problems.cap = 'capEstero';

  const provincia = value('provincia').toUpperCase();
  if (provincia && !/^[A-Z]{2}$/.test(provincia)) problems.provincia = 'provincia';
  else if (!provincia && nazione === NAZIONE_DEFAULT) problems.provincia = 'required';

  if (!/^[A-Z]{2}$/.test(nazione)) problems.nazione = 'nazione';
  return problems;
}
